export const simulateFgtsLoan = ({
  birthDate,
  amount,
  months,
}: {
  birthDate: string
  amount: number
  months?: number
}) => {
  if (!amount || !birthDate || isNaN(amount)) return 0
  if (!months) return simulateFgtsLoanKnowingAvailableAmount({ availableAmount: amount, birthDate })
  return simulateFgtsLoanWithEstimatedAvailableAmount({
    salary: amount,
    monthsWorked: months,
    birthDate,
  })
}

const simulateFgtsLoanKnowingAvailableAmount = ({
  availableAmount,
  birthDate,
}: {
  availableAmount: number
  birthDate: string
}) => calculateAnticipation({ availableAmount, birthDate })

const simulateFgtsLoanWithEstimatedAvailableAmount = ({
  salary,
  monthsWorked,
  birthDate,
}: {
  salary: number
  monthsWorked: number
  birthDate: string
}) => {
  const estimatedAvailableAmount = estimateAvailableAmount({ salary, monthsWorked })
  return calculateAnticipation({ availableAmount: estimatedAvailableAmount, birthDate })
}

function estimateAvailableAmount({
  salary,
  monthsWorked,
}: {
  salary: number
  monthsWorked: number
}) {
  return salary * 0.08 * monthsWorked
}

const PER_MONTH_TAX = 1.69 / 100 // Taxa a.m.
const PER_DAY_TAX = Math.pow(PER_MONTH_TAX + 1, 1 / 30) - 1 // Taxa a.d.
const FINANCIAL_OPERATIONS_TAX = 0.0082 / 100 // Alíquota IOF
const ADDITIONAL_FINANCIAL_OPERATIONS_TAX = 0.38 / 100 // Alíquota IOF Adicional
const MAX_ANTICIPATION_YEARS = 7 // Quantidade máxima de anos considerados no adiantamento
const DAYS_IN_AN_YEAR = 365

function calculateAnticipation({
  availableAmount,
  birthDate,
}: {
  availableAmount: number
  birthDate: string
}) {
  let payDay = getPayDay(birthDate)
  let total = 0
  let year = 1
  while (year <= MAX_ANTICIPATION_YEARS) {
    year += 1
    const installment = getInstallment({ availableAmount })
    const daysUntilPayday = getDaysUntilPayDay(payDay)
    const netPresentValue = installment / Math.pow(1 + PER_DAY_TAX, daysUntilPayday)
    const totalOperationTaxes = getTotalOperationTaxes({
      daysUntilPayday,
      installment,
      netPresentValue,
    })
    const totalWithoutOperationTaxes = netPresentValue - totalOperationTaxes
    total += totalWithoutOperationTaxes
    payDay = getNextYear(payDay)
    availableAmount -= installment
  }
  return total.toFixed(2)
}

const isPayDayAfterAnYear = ({ daysUntilPayday }: { daysUntilPayday: number }) =>
  daysUntilPayday > DAYS_IN_AN_YEAR

const getTotalFinancialOperationsTaxForPaymentInTheCurrentYear = ({
  daysUntilPayday,
  fees,
}: {
  daysUntilPayday: number
  fees: number
}) => daysUntilPayday * fees * FINANCIAL_OPERATIONS_TAX

const getTotalFinancialOperationsTaxForPaymentInTheNextYears = ({
  netPresentValue,
}: {
  netPresentValue: number
}) => FINANCIAL_OPERATIONS_TAX * DAYS_IN_AN_YEAR * netPresentValue

const calculateTotalFinancialOperationsTax = ({
  daysUntilPayday,
  netPresentValue,
  installment,
}: {
  daysUntilPayday: number
  netPresentValue: number
  installment: number
}) => {
  const fees = installment - netPresentValue
  return isPayDayAfterAnYear({ daysUntilPayday })
    ? getTotalFinancialOperationsTaxForPaymentInTheNextYears({ netPresentValue })
    : getTotalFinancialOperationsTaxForPaymentInTheCurrentYear({ daysUntilPayday, fees })
}

const calculateTotalAdditionalFinancialOperationsTax = ({
  netPresentValue,
}: {
  netPresentValue: number
}) => netPresentValue * ADDITIONAL_FINANCIAL_OPERATIONS_TAX

const getTotalOperationTaxes = ({
  daysUntilPayday,
  installment,
  netPresentValue,
}: {
  daysUntilPayday: number
  netPresentValue: number
  installment: number
}) => {
  const totalFinancialOperationsTax = calculateTotalFinancialOperationsTax({
    daysUntilPayday,
    installment,
    netPresentValue,
  })
  const totalAdditionalFinancialOperationsTax = calculateTotalAdditionalFinancialOperationsTax({
    netPresentValue,
  })
  return totalFinancialOperationsTax + totalAdditionalFinancialOperationsTax
}

const getInstallment = ({ availableAmount }: { availableAmount: number }) => {
  const anticipationRate = getAnticipationRate(availableAmount)
  return anticipationRate!.additional + availableAmount * anticipationRate!.aliquot
}

const ANTICIPATION_RATES = [
  {
    check: (balance: number) => balance >= 0 && balance <= 500,
    aliquot: 50 / 100,
    additional: 0,
  },
  {
    check: (balance: number) => balance >= 500.01 && balance <= 1000,
    aliquot: 40 / 100,
    additional: 50,
  },
  {
    check: (balance: number) => balance >= 1000.01 && balance <= 5000,
    aliquot: 30 / 100,
    additional: 150,
  },
  {
    check: (balance: number) => balance >= 5000.01 && balance <= 10000,
    aliquot: 20 / 100,
    additional: 650,
  },
  {
    check: (balance: number) => balance >= 10000.01 && balance <= 15000,
    aliquot: 15 / 100,
    additional: 1150,
  },
  {
    check: (balance: number) => balance >= 15000.01 && balance <= 20000,
    aliquot: 10 / 100,
    additional: 1900,
  },
  {
    check: (balance: number) => balance >= 20000.01,
    aliquot: 5 / 100,
    additional: 2900,
  },
]

function getAnticipationRate(availableAmount: number) {
  if (availableAmount < 0) throw new Error('Não há saldo disponível para saque.')
  return ANTICIPATION_RATES.find((rate) => rate.check(availableAmount))
}

function getBirthday(unformattedBirthDateString: string) {
  const splittedBirthDateString = unformattedBirthDateString.split('/')
  const birthDateStringInYyyyMmDd = `${splittedBirthDateString[2]}-${splittedBirthDateString[1]}-${splittedBirthDateString[0]}`
  const birthDate = new Date(birthDateStringInYyyyMmDd)
  if (birthDate.toString() === 'Invalid Date' || birthDate > new Date())
    throw new Error('Data de nascimento inválida.')
  return { birthMonth: birthDate.getMonth(), birthDay: birthDate.getDate() + 1 }
}

function getPayDay(unformattedBirthDate: string) {
  const { birthDay, birthMonth } = getBirthday(unformattedBirthDate)
  const currentYear = new Date().getFullYear()
  const birthdayOnCurrentYear = new Date(currentYear, birthMonth, birthDay)
  const isBirthdayHappeningThisYear = birthdayOnCurrentYear > new Date()
  const birthdayOnNextYear = new Date(currentYear + 1, birthMonth, birthDay)
  return isBirthdayHappeningThisYear ? birthdayOnCurrentYear : birthdayOnNextYear
}

function getDaysUntilPayDay(payDay: Date) {
  const days = (payDay.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  return Math.ceil(days)
}

function getNextYear(payDay: Date) {
  const payMonth = payDay.getMonth()
  const payDate = payDay.getDate()
  const payYear = payDay.getFullYear()
  return new Date(payYear + 1, payMonth, payDate)
}
